import { Chess, type Square } from "chess.js";

export const START_FEN = "rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1";

export type PlayedMove = { san: string; uci: string; from: Square; to: Square; fen: string; captured?: boolean; check?: boolean };

export type GameResult = { winner: "w" | "b" | null; reason: string };

/** Result for a finished position, or null if the game goes on. */
export function gameResult(c: Chess): GameResult | null {
  if (c.isCheckmate()) return { winner: c.turn() === "w" ? "b" : "w", reason: "checkmate" };
  if (c.isStalemate()) return { winner: null, reason: "stalemate" };
  if (c.isInsufficientMaterial()) return { winner: null, reason: "insufficient material" };
  if (c.isThreefoldRepetition()) return { winner: null, reason: "threefold repetition" };
  if (c.isDrawByFiftyMoves()) return { winner: null, reason: "fifty-move rule" };
  return null;
}

/** Rebuilds a game from a start FEN and UCI moves, so repetition history is exact. */
export function replay(startFen: string, uciMoves: string[]): Chess {
  const c = new Chess(startFen);
  for (const u of uciMoves) c.move({ from: u.slice(0, 2), to: u.slice(2, 4), promotion: u[4] });
  return c;
}

/** Plays a UCI move and returns the record, or null if it is illegal. */
export function applyUci(c: Chess, uci: string): PlayedMove | null {
  try {
    const m = c.move({ from: uci.slice(0, 2), to: uci.slice(2, 4), promotion: uci[4] });
    return { san: m.san, uci: m.lan, from: m.from, to: m.to, fen: c.fen(), captured: !!m.captured, check: c.inCheck() };
  } catch {
    return null;
  }
}

/** Movetext in PGN style: "1. e4 e5 2. Nf3". */
export function movetext(moves: { san: string }[], startFen = START_FEN): string {
  const blackFirst = startFen.split(" ")[1] === "b";
  let n = Number(startFen.split(" ")[5] ?? 1);
  const out: string[] = [];
  moves.forEach((m, i) => {
    const whiteMove = blackFirst ? i % 2 === 1 : i % 2 === 0;
    if (whiteMove) out.push(`${n}.`);
    else if (i === 0) out.push(`${n}...`);
    out.push(m.san);
    if (!whiteMove) n++;
  });
  return out.join(" ");
}

export function toPgn(moves: { san: string }[], headers: Record<string, string>, startFen = START_FEN): string {
  const h = { ...headers };
  if (startFen !== START_FEN) {
    h.SetUp = "1";
    h.FEN = startFen;
  }
  const head = Object.entries(h)
    .map(([k, v]) => `[${k} "${v.replace(/"/g, "'")}"]`)
    .join("\n");
  return `${head}\n\n${movetext(moves, startFen)} ${h.Result ?? "*"}\n`;
}
